import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../config/api';
import toast from 'react-hot-toast';
import { FaMapMarkerAlt, FaClock, FaUser, FaRupeeSign, FaEye } from 'react-icons/fa';

const AdminBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: '',
    paymentStatus: ''
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pages: 1,
    total: 0
  });

  useEffect(() => {
    fetchBookings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, pagination.current]);

  const fetchBookings = async () => {
    try {
      const params = {
        page: pagination.current,
        limit: 10,
        ...filters
      };
      
      const response = await api.get('/api/admin/bookings', { params });
      setBookings(response.data.bookings);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Fetch bookings error:', error);
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20 md:pb-8">
      <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-8">
        <div className="max-w-7xl mx-auto">
          <div className="mb-6 sm:mb-8">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">Booking Management</h1>
            <p className="text-sm sm:text-base text-gray-600 mt-1">Monitor and manage all bookings on the platform</p>
          </div>

          {/* Filters */}
          <div className="card mb-4 sm:mb-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Booking Status</label>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="input-field"
                >
                  <option value="">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="accepted">Accepted</option>
                  <option value="rejected">Rejected</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Payment Status</label>
                <select
                  value={filters.paymentStatus}
                  onChange={(e) => handleFilterChange('paymentStatus', e.target.value)}
                  className="input-field"
                >
                  <option value="">All Payment Status</option>
                  <option value="pending">Pending</option>
                  <option value="paid">Paid</option>
                  <option value="refunded">Refunded</option>
                  <option value="failed">Failed</option>
                </select>
              </div>
              <div className="flex items-end">
                <button
                  onClick={() => setFilters({ status: '', paymentStatus: '' })}
                  className="btn-secondary w-full"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          </div>

          {/* Bookings List */}
          <div className="space-y-3 sm:space-y-4">
            {bookings.map((booking) => (
              <div key={booking._id} className="card">
                <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                  <div className="flex-1 w-full">
                    {/* Passenger and Rider Info */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-4">
                      <div>
                        <h4 className="font-semibold text-gray-800 mb-2">Passenger</h4>
                        <div className="flex items-center">
                          <img
                            src={booking.passenger?.photo || '/default-avatar.png'}
                            alt={booking.passenger?.name}
                            className="w-10 h-10 rounded-full mr-3"
                          />
                          <div>
                            <div className="font-medium">{booking.passenger?.name}</div>
                            <div className="text-sm text-gray-600">{booking.passenger?.email}</div>
                          </div>
                        </div>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-800 mb-2">Rider</h4>
                        <div className="flex items-center">
                          <img
                            src={booking.rider?.photo || '/default-avatar.png'}
                            alt={booking.rider?.name}
                            className="w-10 h-10 rounded-full mr-3"
                          />
                          <div>
                            <div className="font-medium">{booking.rider?.name}</div>
                            <div className="text-sm text-gray-600">{booking.rider?.email}</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Route */}
                    <div className="mb-4">
                      <div className="flex items-center mb-2">
                        <FaMapMarkerAlt className="text-green-500 mr-2" />
                        <span className="font-medium">{booking.trip?.origin?.address}</span>
                      </div>
                      <div className="flex items-center">
                        <FaMapMarkerAlt className="text-red-500 mr-2" />
                        <span className="font-medium">{booking.trip?.destination?.address}</span>
                      </div>
                    </div>

                    {/* Booking Details */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-4">
                      <div className="flex items-center text-gray-600">
                        <FaClock className="mr-2" />
                        <span>{new Date(booking.trip?.departureTime).toLocaleString()}</span>
                      </div>
                      <div className="flex items-center text-gray-600">
                        <FaRupeeSign className="mr-2" />
                        <span className="font-semibold">₹{booking.amount}</span>
                      </div>
                      <div className="text-gray-600">
                        Booked: {new Date(booking.createdAt).toLocaleDateString()}
                      </div>
                      <div className="text-gray-600">
                        {booking.tripStartTime && (
                          <span>Started: {new Date(booking.tripStartTime).toLocaleString()}</span>
                        )}
                      </div>
                    </div>

                    {/* Status */}
                    <div className="flex space-x-2 mb-4">
                      <span className={`badge ${
                        booking.status === 'pending' ? 'badge-warning' :
                        booking.status === 'accepted' ? 'badge-success' :
                        booking.status === 'rejected' ? 'badge-danger' :
                        booking.status === 'cancelled' ? 'badge-danger' : 'badge-success'
                      }`}>
                        {booking.status.toUpperCase()}
                      </span>
                      <span className={`badge ${
                        booking.paymentStatus === 'pending' ? 'badge-warning' :
                        booking.paymentStatus === 'paid' ? 'badge-success' :
                        booking.paymentStatus === 'refunded' ? 'badge-info' : 'badge-danger'
                      }`}>
                        {booking.paymentStatus.toUpperCase()}
                      </span>
                    </div>

                    {/* Notes */}
                    {booking.notes && (
                      <div className="mb-4">
                        <h5 className="font-semibold text-gray-800 mb-1">Notes</h5>
                        <p className="text-gray-600 text-sm">{booking.notes}</p>
                      </div>
                    )}

                    {/* Ratings */}
                    {(booking.passengerRating?.rating || booking.riderRating?.rating) && (
                      <div className="mb-4">
                        <h5 className="font-semibold text-gray-800 mb-2">Ratings</h5>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {booking.passengerRating?.rating && (
                            <div>
                              <div className="text-sm text-gray-600">Passenger Rating</div>
                              <div className="text-sm">{booking.passengerRating.rating}/5</div>
                              {booking.passengerRating.review && (
                                <div className="text-xs text-gray-500 italic">"{booking.passengerRating.review}"</div>
                              )}
                            </div>
                          )}
                          {booking.riderRating?.rating && (
                            <div>
                              <div className="text-sm text-gray-600">Rider Rating</div>
                              <div className="text-sm">{booking.riderRating.rating}/5</div>
                              {booking.riderRating.review && (
                                <div className="text-xs text-gray-500 italic">"{booking.riderRating.review}"</div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="ml-6 flex flex-col space-y-2">
                    <a
                      href={`/trip/${booking.trip?._id}`}
                      className="btn-secondary text-sm flex items-center justify-center"
                    >
                      <FaEye className="mr-2" />
                      View Trip
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="mt-6 flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing {((pagination.current - 1) * 10) + 1} to {Math.min(pagination.current * 10, pagination.total)} of {pagination.total} results
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setPagination(prev => ({ ...prev, current: prev.current - 1 }))}
                  disabled={pagination.current === 1}
                  className="btn-secondary disabled:opacity-50"
                >
                  Previous
                </button>
                <span className="px-3 py-2 text-sm text-gray-700">
                  Page {pagination.current} of {pagination.pages}
                </span>
                <button
                  onClick={() => setPagination(prev => ({ ...prev, current: prev.current + 1 }))}
                  disabled={pagination.current === pagination.pages}
                  className="btn-secondary disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}

          {bookings.length === 0 && (
            <div className="text-center py-12">
              <FaUser className="mx-auto text-gray-400 text-6xl mb-4" />
              <h3 className="text-xl font-semibold text-gray-600 mb-2">
                No bookings found
              </h3>
              <p className="text-gray-500">
                {filters.status || filters.paymentStatus 
                  ? 'No bookings match your current filters.'
                  : 'No bookings have been made yet.'
                }
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminBookings;
